import {
  Activity,
  BookOpen,
  Building2,
  CalendarDays,
  CircleHelp,
  ClipboardList,
  CreditCard,
  House,
  Menu,
  MessageSquare,
  Moon,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Users,
  Wallet,
  X,
  createElement,
  type IconNode,
} from "lucide";

const routeIcons: Record<string, IconNode> = {
  home: House,
  dashboard: House,
  overview: House,
  people: Users,
  events: CalendarDays,
  announcements: MessageSquare,
  content: BookOpen,
  "member-support": CircleHelp,
  "shdeed-support": CircleHelp,
  "profile-audit": ClipboardList,
  communications: MessageSquare,
  support: CircleHelp,
  ramadan: Moon,
  modes: SlidersHorizontal,
  operationalModes: SlidersHorizontal,
  settings: Settings,
  organization: Building2,
  organizations: Building2,
  commerce: Wallet,
  billing: CreditCard,
  directory: Store,
  islamic: BookOpen,
  operations: Activity,
  diagnostics: Activity,
  audit: ClipboardList,
  migration: ShieldCheck,
  product: SlidersHorizontal,
};

export function normalizedSectionSearch(value: string): string {
  return value.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase().trim();
}

export function enhanceAdminWorkspace(root: HTMLElement, locale: string): void {
  const sidebar = root.querySelector<HTMLElement>(".sidebar");
  const nav = sidebar?.querySelector<HTMLElement>(".nav");
  if (!sidebar || !nav || sidebar.querySelector(".workspace-menu-toggle")) return;
  const arabic = locale === "ar";
  nav.id = "workspace-navigation";
  nav.setAttribute("aria-label", arabic ? "أقسام الإدارة" : "Workspace sections");

  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "workspace-menu-toggle secondary-action";
  toggle.title = arabic ? "القائمة" : "Menu";
  toggle.setAttribute("aria-label", toggle.title);
  toggle.setAttribute("aria-controls", nav.id);
  toggle.setAttribute("aria-expanded", "false");
  const setOpen = (open: boolean) => {
    sidebar.classList.toggle("is-menu-open", open);
    toggle.setAttribute("aria-expanded", String(open));
    toggle.replaceChildren(createElement(open ? X : Menu, { "aria-hidden": "true" }));
  };
  setOpen(false);
  toggle.addEventListener("click", () => setOpen(!sidebar.classList.contains("is-menu-open")));
  sidebar.prepend(toggle);
  sidebar.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && sidebar.classList.contains("is-menu-open")) {
      setOpen(false);
      toggle.focus();
    }
  });

  const searchLabel = document.createElement("label");
  searchLabel.className = "workspace-nav-search";
  const search = document.createElement("input");
  search.type = "search";
  search.placeholder = arabic ? "البحث في الأقسام" : "Find a section";
  search.setAttribute("aria-label", search.placeholder);
  searchLabel.append(createElement(Search, { "aria-hidden": "true" }), search);
  nav.before(searchLabel);
  const empty = document.createElement("p");
  empty.className = "workspace-nav-empty";
  empty.textContent = arabic ? "لا توجد أقسام مطابقة" : "No matching sections";
  empty.hidden = true;
  empty.setAttribute("role", "status");
  nav.after(empty);
  const links = Array.from(nav.querySelectorAll<HTMLAnchorElement>("a"));
  for (const link of links) {
    const route = link.hash.slice(1);
    link.prepend(createElement(routeIcons[route] ?? ClipboardList, { "aria-hidden": "true" }));
    link.addEventListener("click", () => {
      if (!sidebar.classList.contains("is-menu-open")) return;
      setOpen(false);
      const focusHeading = () =>
        requestAnimationFrame(() => {
          const heading = root.querySelector<HTMLElement>(
            ".workspace-page.is-active .page-header h2",
          );
          heading?.setAttribute("tabindex", "-1");
          heading?.focus({ preventScroll: true });
        });
      if (link.hash === window.location.hash) focusHeading();
      else window.addEventListener("hashchange", focusHeading, { once: true });
    });
  }
  search.addEventListener("input", () => {
    const query = normalizedSectionSearch(search.value);
    for (const link of links) {
      link.hidden = !normalizedSectionSearch(link.textContent ?? "").includes(query);
    }
    for (const group of nav.querySelectorAll<HTMLElement>(".nav-group-label")) {
      let sibling = group.nextElementSibling;
      let hasMatch = false;
      while (sibling && !sibling.classList.contains("nav-group-label")) {
        if (sibling instanceof HTMLAnchorElement && !sibling.hidden) hasMatch = true;
        sibling = sibling.nextElementSibling;
      }
      group.hidden = !hasMatch;
    }
    empty.hidden = links.some((link) => !link.hidden);
  });

  const workspace = root.querySelector<HTMLElement>(".workspace");
  const content = workspace?.querySelector<HTMLElement>(".content");
  if (content) {
    content.id = "workspace-content";
    content.tabIndex = -1;
    const skip = document.createElement("a");
    skip.className = "workspace-skip-link";
    skip.href = "#workspace-content";
    skip.textContent = arabic ? "تخطي إلى المحتوى" : "Skip to content";
    skip.addEventListener("click", (event) => {
      event.preventDefault();
      content.focus();
    });
    root.prepend(skip);
  }
}
